export const CREATE_PRODUCT_WITH_STATUS_FIELDS = [
  'id',
  'progress',
  'startDate',
  'endDate',
  'instructions',
  'byUserID',
  'toUserID',
  'name',
  'slug',
  'action',
  'type',
  'placeID',
  'toPlaceID',
  'spot',
  'projectID',
  'draft',
  'category',
  'categories',
  'subCategory',
  'complement',
  'complementEN',
  'complementNL',
  'pictures',
  'color',
  'brand',
  'model',
  'condition',
  'conditions',
  'provenance',
  'provenances',
  'isInsured',
  'description',
  'descriptionEN',
  'descriptionNL',
  'internalNotes',
  'material',
  'weight',
  'co2Impact',
  'wasteCost',
  'wasteCode',
  'valueIndex',
  'reemploymentIndex',
  'reusabilityIndex',
  'recyclabilityIndex',
  'valorisationIndex',
  'eliminationIndex',
  'removalIndex',
  'channels',
  'lot',
  'storage',
  'typology',
  'inies',
  'linkFdes',
  'linkTechnicalSheet',
  'isPromoted',
  'location',
  'city',
  'mapPosition',
  'geometry',
  'groups',
  'seen',
  'quality',
  'ref',
  'referenceID',
  'shopID',
  'availableForSale',
  'supplierID',
  'partner',
  'externalSource',
  'externalUrl',
  'nature',
  'sortIndex',
  'quantity',
  'price',
  'dimension',
  'dimensionRange',
  'properties',
  'careInstructions',
  'instructionAssembly',
  'instructionRemoval',
  'instructionTransport',
  'instructionStorage',
  'instructionConditioning',
  'instructionPotential',
  'instructionSafety',
  'instructionAccess',
  'shapeID',
  'variations',
  'relatedProducts',
  'proofPictures',
] as const

type ProductStatusCreationSource = Record<string, unknown> & {
  id?: unknown
  organizationID?: unknown
  productID?: unknown
}

export type ProductWithStatusCreation = {
  createProductWithStatus: {
    product: { createdAt?: string | null }
    productStatus: {
      id?: string | null
      createdAt?: string | null
      updatedAt?: string | null
    }
  }
}

const requiredString = (value: unknown, field: string): string => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`${field} is required for atomic product creation`)
  }
  return value.trim()
}

const withoutClientProductGroupLabel = (groups: unknown): unknown => {
  if (!Array.isArray(groups)) return groups

  return groups.map((group) => {
    if (typeof group !== 'object' || group === null || Array.isArray(group)) {
      return group
    }

    const candidate = group as Record<string, unknown>
    if (
      typeof candidate.type !== 'string' ||
      candidate.type.toLowerCase() !== 'product'
    ) {
      return group
    }

    const { label: _serverDerived, ...canonicalGroup } = candidate
    return canonicalGroup
  })
}

export const buildCreateProductWithStatusInput = ({
  organizationID,
  productID,
  productName,
  productNature,
  productStatusInput,
}: {
  organizationID?: unknown
  productID?: unknown
  productName?: unknown
  productNature?: unknown
  productStatusInput: ProductStatusCreationSource
}) => {
  const canonicalOrganizationID = requiredString(
    organizationID,
    'organizationID',
  )
  const canonicalProductID = requiredString(productID, 'productID')
  const canonicalProductName = requiredString(productName, 'productName')
  requiredString(productStatusInput.id, 'productStatus.id')
  const canonicalStatusName =
    productStatusInput.name === undefined
      ? undefined
      : requiredString(productStatusInput.name, 'productStatus.name')

  const statusProductID = requiredString(
    productStatusInput.productID,
    'productStatus.productID',
  )
  if (statusProductID !== canonicalProductID) {
    throw new Error('productStatus.productID must match productID')
  }

  if (
    productStatusInput.organizationID !== undefined &&
    productStatusInput.organizationID !== canonicalOrganizationID
  ) {
    throw new Error('productStatus.organizationID must match organizationID')
  }

  if (
    productNature !== undefined &&
    productStatusInput.nature !== undefined &&
    productStatusInput.nature !== productNature
  ) {
    throw new Error('productStatus.nature must match productNature')
  }

  const productStatus = CREATE_PRODUCT_WITH_STATUS_FIELDS.reduce<
    Record<string, unknown>
  >((status, field) => {
    const value =
      field === 'name'
        ? canonicalStatusName
        : field === 'groups'
          ? withoutClientProductGroupLabel(productStatusInput.groups)
          : productStatusInput[field]
    if (value !== undefined) status[field] = value
    return status
  }, {})

  return {
    productID: canonicalProductID,
    productName: canonicalProductName,
    organizationID: canonicalOrganizationID,
    ...(productNature !== undefined ? { productNature } : {}),
    productStatus,
  }
}
